import {CreditCardScheme} from './util/types.ts';
import type {CreditCardSkinAppearance} from './skins/types.ts';
import {DEFAULT_SCHEME_LOGO_APPEARANCE} from './skins/defaultSchemeLogoAppearance.ts';
import {AmexLogo} from './icons/logos/AmexLogo.tsx';
import {DinersLogo} from './icons/logos/DinersLogo.tsx';
import {JcbLogo} from './icons/logos/JcbLogo.tsx';
import {MastercardLogo} from './icons/logos/MastercardLogo.tsx';
import {VisaLogo} from './icons/logos/VisaLogo.tsx';

type LogoSize = 'default' | 'compact';

export interface SchemeLogoProps {
    className?: string;
    color?: string;
}

interface Props {
    scheme: CreditCardScheme;
    size?: LogoSize;
    className?: string;
    appearance?: CreditCardSkinAppearance;
}

const SIZE_CLASSES: Record<LogoSize, Record<CreditCardScheme, string>> = {
    default: {
        [CreditCardScheme.VISA]: 'h-12 w-12',
        [CreditCardScheme.MASTERCARD]: 'h-12 w-12',
        [CreditCardScheme.AMEX]: 'h-6 w-auto',
        [CreditCardScheme.DINERS]: 'h-12 w-12',
        [CreditCardScheme.JCB]: 'h-12 w-12',
    },
    compact: {
        [CreditCardScheme.VISA]: 'h-6 w-10',
        [CreditCardScheme.MASTERCARD]: 'h-5 w-7',
        [CreditCardScheme.AMEX]: 'h-3.5 w-auto',
        [CreditCardScheme.DINERS]: 'h-5 w-7',
        [CreditCardScheme.JCB]: 'h-4 w-7',
    },
};

export function CreditCardSchemeLogo({
    scheme,
    size = 'default',
    className = '',
    appearance = DEFAULT_SCHEME_LOGO_APPEARANCE,
}: Props) {
    const sizeClass = SIZE_CLASSES[size][scheme];

    switch (scheme) {
        case CreditCardScheme.VISA:
            return (
                <VisaLogo
                    className={`${sizeClass} ${className}`.trim()}
                    color={appearance.visaLogoColor}
                />
            );
        case CreditCardScheme.MASTERCARD:
            return <MastercardLogo className={`${sizeClass} ${className}`.trim()} />;
        case CreditCardScheme.AMEX:
            return (
                <AmexLogo
                    className={`${sizeClass} ${className}`.trim()}
                    color={appearance.amexLogoColor}
                />
            );
        case CreditCardScheme.DINERS:
            return (
                <DinersLogo
                    className={`${sizeClass} ${className}`.trim()}
                    color={appearance.dinersLogoColor}
                />
            );
        case CreditCardScheme.JCB:
            return (
                <JcbLogo
                    className={`${sizeClass} ${className}`.trim()}
                    color={appearance.jcbLogoColor}
                />
            );
        default: {
            const _exhaustive: never = scheme;
            return _exhaustive;
        }
    }
}
